import { useState, useCallback } from 'react'
import type { VenuePhoto } from '@/types/venue'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Upload, Image as ImageIcon, Eye, EyeOff, Trash2 } from 'lucide-react'

interface PhotoUploaderProps {
  photos: VenuePhoto[]
  onChange: (photos: VenuePhoto[]) => void
  onUpload: (file: File) => Promise<VenuePhoto>
  authToken?: string // Session token for authenticated image loading
  disabled?: boolean // Disable uploads (e.g., for new unsaved venues)
  disabledMessage?: string // Message to show when disabled
}

/**
 * Helper to get authenticated URL for photos
 *
 * For proxy URLs (/api/vault/venues/files/...), the browser will automatically
 * send the better-auth.session_token cookie which our backend proxy will use
 * to authenticate with the vault.
 *
 * No token manipulation needed - cookies are sent automatically for same-origin requests.
 */
function getAuthenticatedUrl(url: string, _token?: string): string {
  // Just return the URL as-is - cookies will be sent automatically
  return url
}

export function PhotoUploader({ photos, onChange, onUpload, authToken, disabled, disabledMessage }: PhotoUploaderProps) {
  const [isDragging, setIsDragging] = useState(false)
  const [uploading, setUploading] = useState(false)

  const isDisabled = disabled || uploading

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)

    if (disabled) return

    const files = Array.from(e.dataTransfer.files).filter((file) =>
      file.type.startsWith('image/')
    )

    if (files.length > 0) {
      setUploading(true)
      try {
        const newPhotos = await Promise.all(files.map((file) => onUpload(file)))
        onChange([...photos, ...newPhotos])
      } catch (error) {
        console.error('Upload failed:', error)
      } finally {
        setUploading(false)
      }
    }
  }, [photos, onChange, onUpload, disabled])

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files) return

    const imageFiles = Array.from(files).filter((file) =>
      file.type.startsWith('image/')
    )

    if (imageFiles.length > 0) {
      setUploading(true)
      try {
        const newPhotos = await Promise.all(imageFiles.map((file) => onUpload(file)))
        onChange([...photos, ...newPhotos])
      } catch (error) {
        console.error('Upload failed:', error)
      } finally {
        setUploading(false)
      }
    }
  }, [photos, onChange, onUpload])

  const removePhoto = (photoId: string) => {
    onChange(photos.filter((p) => p.id !== photoId))
  }

  const toggleHidden = (photoId: string) => {
    onChange(
      photos.map((p) =>
        p.id === photoId ? { ...p, hidden: !p.hidden } : p
      )
    )
  }

  const updateCaption = (photoId: string, caption: string) => {
    onChange(
      photos.map((p) =>
        p.id === photoId ? { ...p, caption } : p
      )
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Photos</h3>
        <span className="text-sm text-gray-500">{photos.length} photo{photos.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={isDisabled ? undefined : handleDragOver}
        onDragLeave={isDisabled ? undefined : handleDragLeave}
        onDrop={isDisabled ? undefined : handleDrop}
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          isDisabled
            ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
            : isDragging
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-300 hover:border-gray-400'
        }`}
      >
        <ImageIcon className={`h-12 w-12 mx-auto mb-4 ${isDisabled ? 'text-gray-300' : 'text-gray-400'}`} />
        <p className={`mb-2 ${isDisabled ? 'text-gray-400' : 'text-gray-600'}`}>
          {disabled && disabledMessage
            ? disabledMessage
            : 'Drag and drop images here, or click to select'}
        </p>
        <input
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          id="photo-upload"
          disabled={isDisabled}
        />
        <label htmlFor="photo-upload">
          <Button type="button" variant="outline" disabled={isDisabled} asChild>
            <span>
              <Upload className="h-4 w-4 mr-2" />
              {uploading ? 'Uploading...' : 'Select Photos'}
            </span>
          </Button>
        </label>
      </div>

      {/* Photo grid */}
      {photos.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {photos.map((photo) => (
            <div
              key={photo.id}
              className={`relative group rounded-lg border ${
                photo.hidden ? 'border-gray-300 bg-gray-50' : 'border-gray-200 bg-white'
              } overflow-hidden shadow-sm`}
            >
              {/* Image container */}
              <div className="relative">
                <img
                  src={getAuthenticatedUrl(photo.url, authToken)}
                  alt={photo.caption || photo.filename}
                  className={`w-full h-40 object-cover ${photo.hidden ? 'opacity-40 grayscale' : ''}`}
                />
                {photo.hidden && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="bg-gray-800/70 text-white text-xs px-2 py-1 rounded">
                      Hidden from carousel
                    </span>
                  </div>
                )}
                {/* Action buttons overlay - always visible for easy management */}
                <div className="absolute top-2 right-2 flex gap-1">
                  <button
                    type="button"
                    onClick={() => toggleHidden(photo.id)}
                    className={`p-1.5 rounded-full shadow-md transition-colors ${
                      photo.hidden
                        ? 'bg-gray-600 text-white hover:bg-gray-700'
                        : 'bg-white/90 text-gray-600 hover:bg-white'
                    }`}
                    title={photo.hidden ? 'Show in carousel' : 'Hide from carousel'}
                  >
                    {photo.hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                  <button
                    type="button"
                    onClick={() => removePhoto(photo.id)}
                    className="p-1.5 bg-red-500 text-white rounded-full shadow-md hover:bg-red-600 transition-colors"
                    title="Delete photo"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Caption and filename */}
              <div className="p-3 space-y-2">
                <Input
                  type="text"
                  placeholder="Add a caption..."
                  value={photo.caption || ''}
                  onChange={(e) => updateCaption(photo.id, e.target.value)}
                  className="text-sm"
                  maxLength={200}
                />
                <p className="text-xs text-gray-400 truncate" title={photo.filename}>
                  {photo.filename}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Stats */}
      {photos.length > 0 && (
        <div className="text-sm text-gray-500 flex gap-4">
          <span>{photos.filter(p => !p.hidden).length} visible in carousel</span>
          {photos.filter(p => p.hidden).length > 0 && (
            <span>{photos.filter(p => p.hidden).length} hidden</span>
          )}
        </div>
      )}
    </div>
  )
}
